import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BillingAgreement, Currency } from '@freelancer/datastore/collections';
import { Deposits } from '@freelancer/deposits';
import {
  PaymentsErrorType,
  PaymentsMessagingService,
  PaymentsResultStatus,
} from '@freelancer/payments-messaging';
import { PaymentsUtils } from '@freelancer/payments-utils';
import { ThreatmetrixService } from '@freelancer/threatmetrix';
import { PictureDisplay, PictureImage } from '@freelancer/ui/picture';
import { DepositMethodApi } from 'api-typings/payments/payments';
import { SourceDisplayMode } from '../../../../common/source-template/source-template.component';
import { DepositData } from '../../../../common/types';

@Component({
  selector: 'app-credit-card-billing-agreement',
  template: `
    <app-source-template
      [group]="group"
      [sourceRadioValue]="agreement.id"
      [sourceDisplayMode]="SourceDisplayMode.LIST_ITEM_WITH_HEADER"
    >
      <app-source-template-header>
        <app-source-template-header-title>
          {{ agreement?.creditCard?.creditCardNumber }} ({{
            billingAgreementCurrency?.code
          }})
        </app-source-template-header-title>
        <app-source-template-logo [image]="logo"></app-source-template-logo>
      </app-source-template-header>
    </app-source-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./credit-card-billing-agreement.component.scss'],
})
export class CreditCardBillingAgreementComponent implements OnInit {
  PictureDisplay = PictureDisplay;
  SourceDisplayMode = SourceDisplayMode;

  logo: PictureImage;
  billingAgreementCurrency: Currency | undefined;

  private _agreement: BillingAgreement;

  @Input() group: FormGroup;
  @Input() currencies: readonly Currency[];
  @Input() cartId?: number;
  @Input() trackingToken: string;
  @Input() set depositData(depositData: DepositData) {
    if (depositData) {
      this.handleDepositDataChanges(depositData);
    }
  }
  @Input() set agreement(agreement: BillingAgreement) {
    this._agreement = agreement;
    if (agreement) {
      this.handleBillingAgreementChanges(agreement);
    }
  }

  get agreement() {
    return this._agreement;
  }

  @Output() paymentCurrencySelected = new EventEmitter<Currency>();

  constructor(
    private th: ThreatmetrixService,
    private paymentsMessaging: PaymentsMessagingService,
    private deposits: Deposits,
    private paymentsUtils: PaymentsUtils,
  ) {}

  ngOnInit() {
    this.billingAgreementCurrency = this.currencies.find(
      c => c.id === this.agreement.currencyId,
    );
  }

  handleDepositDataChanges(depositData: DepositData) {
    this.deposits
      .authorization(
        DepositMethodApi.FLN_BILLING,
        this.agreement.id,
        depositData.depositAmount,
        depositData.depositCurrency.id,
        depositData.paymentCurrency.id,
        this.th.getSession(),
        this.trackingToken,
        this.cartId,
        this.agreement.creditCard
          ? this.agreement.creditCard.gateway
          : undefined,
      )
      .toPromise()
      .then(result => {
        if (result.status === 'success') {
          this.paymentsMessaging.pushResult({
            paymentsStatus: PaymentsResultStatus.SUCCESS,
          });
        } else {
          this.paymentsMessaging.pushError({
            errorCode: result.errorCode,
            errorType: PaymentsErrorType.ERROR,
            eventId: result.requestId,
          });
        }
      })
      .catch(error => {
        this.paymentsMessaging.pushError({
          errorType: PaymentsErrorType.ERROR_UNKNOWN,
          errorMessage: error.message,
        });
      });
  }

  handleBillingAgreementChanges(agreement: BillingAgreement) {
    this.logo =
      agreement.creditCard && agreement.creditCard.cardType
        ? {
            src: this.paymentsUtils.getMethodLogoPath(
              agreement.creditCard.cardType,
            ),
            alt: `${agreement.creditCard.cardType || 'credit card'} logo`,
          }
        : {
            src: this.paymentsUtils.getMethodLogoPath(agreement.depositMethod),
            alt: 'credit card logo',
          };
  }
}
